import {
  Event,
  isMeetingEvent,
  MeetingActivity,
} from 'crono-fe-common/types/event';
import { isAfter, isValid, parseISO, startOfDay } from 'date-fns';
import { toUtcCalendarDay } from 'utils/dateUtils';

export function buildAttendeeEmails(
  currentUserEmail: string | null | undefined,
  contactEmails: (string | null | undefined)[],
): string[] {
  return [
    ...new Set([currentUserEmail, ...contactEmails].filter(Boolean)),
  ] as string[];
}

export function getMeetingInfo(
  activity?: Event | null,
): MeetingActivity | null {
  if (!activity) {
    return null;
  }

  if (activity.meeting) {
    return activity.meeting;
  } else {
    return {
      id: activity.id,
      provider: 'Google',
      title: activity.subject,
      startTime: activity.activityDateTime,
      endTime: activity.endDateTime,
      isAllDay: activity.isAllDayEvent ?? false,
      description: activity.description,
      // Meetings without a calendar event have no calendar page to open.
      htmlLink: null,
      location: activity.location,
      attendeeEmails: activity.attendeeEmails,
      conferenceLink: activity.conferenceLink,
      organizerEmail: null,
      conferencePhoneNumber: null,
      conferencePhonePin: null,
      calendarId: null,
    };
  }
}

const parseValidDate = (isoDate?: string | null): Date | null => {
  if (!isoDate) {
    return null;
  }
  const parsed = parseISO(isoDate);
  return isValid(parsed) ? parsed : null;
};

export const isMeetingCompleted = (
  event?: Event | MeetingActivity | null,
): boolean => {
  const meetingInfo = isMeetingEvent(event)
    ? getMeetingInfo(event)
    : (event as MeetingActivity | null | undefined);

  const start = parseValidDate(meetingInfo?.startTime);
  if (!start) {
    return true;
  }

  const end = parseValidDate(meetingInfo?.endTime);
  const now = new Date();

  // An all-day meeting is a floating calendar date stored as UTC midnight, so
  // its day is read in UTC — but "is that day over?" is asked in the viewer's
  // own day.
  if (meetingInfo?.isAllDay) {
    return isAfter(startOfDay(now), toUtcCalendarDay(end ?? start));
  }

  // Without an end the duration is unknown, so the meeting stays current for
  // the rest of its day instead of being declared over the instant it starts.
  if (!end) {
    return isAfter(startOfDay(now), startOfDay(start));
  }

  return !isAfter(end, now);
};
